import { useEffect, useState } from 'react';

// Which platform apps the selected agent is bound to (Wix / Shopify), as the
// Knowledge Base sidebar and the provider pages need it.
//
// Backed by GET apps/bindings — a DB-only endpoint, NOT /api/integrations, which
// probes Wix over the network to prove the app is still installed. The sidebar
// asks this on every agent switch, so it has to stay cheap.
//
// A module-level cache keeps it to ONE request per agent for the whole tab: the
// nav bridge, the Wix page and the Shopify page all read the same entry instead
// of each firing their own status call (the mistake the three independent
// apps/shopify/status callers already make — see the known issue in
// specs/platform-apps/shopify-surfaces.yaml).
//
// Staleness is deliberate and safe: a binding that died since (app uninstalled)
// only means the leaf is offered one more time, and opening it loads the real
// provider hub, which resolves the true install state and shows the reinstall
// prompt. Call invalidateAppBindings(botId) after a connect / disconnect so the
// leaf appears or disappears without a reload.

const API = process.env.REACT_APP_API_BASE_URL;

const EMPTY = {
  wix: false, shopify: false, shopify_shop: null,
  wix_manage_url: null, shopify_manage_url: null,
};

const cache = new Map();     // botId -> bindings
const inflight = new Map();  // botId -> Promise<bindings>
const listeners = new Set(); // () => void, notified when an entry changes

function notify() {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* a dead subscriber must not break the others */ }
  });
}

export function invalidateAppBindings(botId) {
  if (botId) {
    cache.delete(botId);
    inflight.delete(botId);
  } else {
    cache.clear();
    inflight.clear();
  }
  notify();
}

function load(botId) {
  if (cache.has(botId)) return Promise.resolve(cache.get(botId));
  if (inflight.has(botId)) return inflight.get(botId);
  const p = fetch(`${API}apps/bindings?bot_id=${encodeURIComponent(botId)}`)
    .then((res) => (res.ok ? res.json() : EMPTY))
    .catch(() => EMPTY)
    .then((data) => {
      const value = {
        wix: !!(data && data.wix),
        shopify: !!(data && data.shopify),
        shopify_shop: (data && data.shopify_shop) || null,
        wix_manage_url: (data && data.wix_manage_url) || null,
        shopify_manage_url: (data && data.shopify_manage_url) || null,
      };
      cache.set(botId, value);
      inflight.delete(botId);
      notify();
      return value;
    });
  inflight.set(botId, p);
  return p;
}

export default function useAppBindings(botId) {
  const [bindings, setBindings] = useState(() => (botId && cache.get(botId)) || EMPTY);

  useEffect(() => {
    if (!botId) { setBindings(EMPTY); return undefined; }
    let alive = true;
    const sync = () => {
      if (!alive) return;
      const hit = cache.get(botId);
      if (hit) setBindings(hit);
      else load(botId).then((v) => { if (alive) setBindings(v); });
    };
    sync();
    listeners.add(sync);
    return () => { alive = false; listeners.delete(sync); };
  }, [botId]);

  return bindings;
}
